import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import {
  eatDayOf,
  formatDay,
  formatIsoDay,
  parseIsoDay,
  requestDueOn,
  startOfEatDay,
  STATUTORY_DAYS,
} from '@wodi/domain';
import {
  RECORDS_CORRECTION_AREAS,
  RECORDS_REQUEST_CHANNELS,
  RECORDS_REQUEST_KINDS,
  RECORDS_REQUESTER_RELATIONSHIPS,
  type CreateRecordsRequestBody,
} from '@wodi/shared';
import {
  Button,
  DateField,
  Notice,
  PageHeader,
  RadioGroup,
  Section,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { logRecordsRequest } from '../api/records.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ShortcutHint } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import {
  AREA_WORDS,
  CHANNEL_WORDS,
  KIND_DESCRIPTIONS,
  KIND_WORDS,
  RELATIONSHIP_WORDS,
} from './labels.ts';
import { errorText } from './records-page.tsx';
import styles from './records.module.css';

// Log a data-subject request for a patient (docs/plans/phase2-records-data-rights.md §6).
// The due date follows from the kind and the day it was received (TODO(legal-verify) R1).

const route = getRouteApi('/_app/patients/$patientId/records-requests/new');

type Kind = CreateRecordsRequestBody['kind'];
type Area = NonNullable<CreateRecordsRequestBody['correctionArea']>;
type Relationship = CreateRecordsRequestBody['requesterRelationship'];
type Channel = CreateRecordsRequestBody['channel'];

/** Today's requests keep the time they were logged; an earlier day is the start of that day. */
function receivedAtFor(day: string, now: Date): string {
  if (day === formatIsoDay(eatDayOf(now))) return now.toISOString();
  const parsed = parseIsoDay(day);
  return (parsed.ok ? startOfEatDay(parsed.value) : now).toISOString();
}

export function NewRecordsRequestPage() {
  const { patientId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = formatIsoDay(eatDayOf(new Date()));
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [kind, setKind] = useState<Kind | null>(null);
  const [area, setArea] = useState<Area | null>(null);
  const [relationship, setRelationship] = useState<Relationship>('self');
  const [requesterName, setRequesterName] = useState('');
  const [channel, setChannel] = useState<Channel>('in_person');
  const [receivedOn, setReceivedOn] = useState<string | null>(today);
  const [details, setDetails] = useState('');
  const mayLog = session !== null && can(session, 'records.requests.manage');

  const log = useMutation({
    mutationFn: () =>
      logRecordsRequest(patientId, {
        clientRequestId,
        kind: kind ?? 'access',
        ...(kind === 'correction' && area !== null ? { correctionArea: area } : {}),
        requesterRelationship: relationship,
        ...(relationship === 'self' ? {} : { requesterName: requesterName.trim() }),
        channel,
        receivedAt: receivedAtFor(receivedOn ?? today, new Date()),
        details: details.trim(),
      }),
    onSuccess: async (request) => {
      await queryClient.invalidateQueries({ queryKey: ['records'] });
      toasts.show({ title: 'Request logged', description: request.number });
      await navigate({ to: '/records/requests/$requestId', params: { requestId: request.id } });
    },
  });

  const ready =
    online &&
    !log.isPending &&
    kind !== null &&
    (kind !== 'correction' || area !== null) &&
    (relationship === 'self' || requesterName.trim() !== '') &&
    receivedOn !== null &&
    details.trim() !== '';
  useShortcut(
    'mod+enter',
    () => {
      if (ready) log.mutate();
    },
    { allowInFields: true, enabled: mayLog, description: 'Log request' },
  );

  const received = receivedOn === null ? null : parseIsoDay(receivedOn);
  const dueText =
    kind === null || !received?.ok
      ? null
      : `Due by ${formatDay(requestDueOn(kind, received.value))}: ${String(STATUTORY_DAYS[kind])} days after it was received (provisional, pending legal review).`;

  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={patientId} />
      <PageHeader
        title="Log a records request"
        description="Write down what the person asked for. Their identity is checked next, before anything is released or changed."
      />
      {!mayLog ? (
        <Notice tone="info">
          You don't have permission to log records requests. Ask the medical records office.
        </Notice>
      ) : (
        <Section title="The request">
          <form
            className={styles.form}
            aria-label="Log a records request"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) log.mutate();
            }}
          >
            <RadioGroup
              label="What are they asking for?"
              options={RECORDS_REQUEST_KINDS.map((k) => ({
                id: k,
                label: KIND_WORDS[k],
                description: KIND_DESCRIPTIONS[k],
              }))}
              value={kind}
              onChange={(v) => {
                setKind(v as Kind);
              }}
            />
            {kind === 'correction' ? (
              <RadioGroup
                label="What is wrong?"
                description="Personal details are corrected through registration; clinical records get a new version or are marked entered in error. Nothing is deleted."
                options={RECORDS_CORRECTION_AREAS.map((a) => ({ id: a, label: AREA_WORDS[a] }))}
                value={area}
                onChange={(v) => {
                  setArea(v as Area);
                }}
              />
            ) : null}
            {kind === 'deletion' ? (
              <Notice tone="info">
                Health records are never deleted. The request is recorded and answered with the
                retention explanation.
              </Notice>
            ) : null}
            <RadioGroup
              label="Who is asking?"
              options={RECORDS_REQUESTER_RELATIONSHIPS.map((r) => ({
                id: r,
                label: RELATIONSHIP_WORDS[r],
              }))}
              value={relationship}
              onChange={(v) => {
                setRelationship(v as Relationship);
              }}
            />
            {relationship === 'self' ? null : (
              <TextField
                label="Their name"
                width="md"
                value={requesterName}
                onChange={setRequesterName}
              />
            )}
            <RadioGroup
              label="How did it arrive?"
              options={RECORDS_REQUEST_CHANNELS.map((c) => ({ id: c, label: CHANNEL_WORDS[c] }))}
              value={channel}
              onChange={(v) => {
                setChannel(v as Channel);
              }}
            />
            <DateField
              label="Received on"
              value={receivedOn}
              onChange={setReceivedOn}
              maxValue={today}
              {...(dueText === null ? {} : { description: dueText })}
            />
            <TextArea
              label="What did they ask for?"
              description="In their words. Kept with the request; never logged."
              value={details}
              onChange={setDetails}
              rows={4}
            />
            {!online ? (
              <Notice tone="info">
                No connection to Wodi. Logging waits for the connection; nothing is saved on this
                device.
              </Notice>
            ) : null}
            {log.error === null ? null : (
              <Notice tone="error">
                {errorText(log.error, "The request wasn't logged. Try again.")}
              </Notice>
            )}
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!ready}
                isPending={log.isPending}
                pendingLabel="Logging…"
              >
                Log request
              </Button>
              <ShortcutHint combo="mod+enter" action="log the request" />
            </div>
          </form>
        </Section>
      )}
    </div>
  );
}
